// easing.js -- shared easing functions for control plane
// used by the controlXY and o2p preset engines and by o2p's ease:
// © 2025 Rob Canning — GPLv3

export const easings = {
  linear: t => t,
  easeInSine: t => 1 - Math.cos((t * Math.PI) / 2),
  easeOutSine: t => Math.sin((t * Math.PI) / 2),
  easeInOutSine: t => -(Math.cos(Math.PI * t) - 1) / 2,
  easeInQuad: t => t * t,
  easeOutQuad: t => 1 - (1 - t) * (1 - t),
  easeInOutQuad: t => t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2,
  easeInCubic: t => t * t * t,
  easeOutCubic: t => 1 - Math.pow(1 - t, 3),
  easeInOutCubic: t => t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,
  easeInBack: t => 2.70158 * t * t * t - 1.70158 * t * t,
  easeOutBack: t => 1 + 2.70158 * Math.pow(t - 1, 3) + 1.70158 * Math.pow(t - 1, 2),
  easeInOutBack: t => {
    const c1 = 1.70158, c2 = c1 * 1.525;
    return t < 0.5
      ? (Math.pow(2 * t, 2) * ((c2 + 1) * 2 * t - c2)) / 2
      : (Math.pow(2 * t - 2, 2) * ((c2 + 1) * (t * 2 - 2) + c2) + 2) / 2;
  },
  easeInElastic: t => t === 0 ? 0 : t === 1 ? 1
    : -Math.pow(2, 10 * t - 10) * Math.sin((t * 10 - 10.75) * ((2 * Math.PI) / 3)),
  easeOutElastic: t => t === 0 ? 0 : t === 1 ? 1
    : Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * ((2 * Math.PI) / 3)) + 1,
  easeInOutElastic: t => t === 0 ? 0 : t === 1 ? 1 : t < 0.5
    ? -(Math.pow(2, 20 * t - 10) * Math.sin((20 * t - 11.125) * ((2 * Math.PI) / 4.5))) / 2
    : (Math.pow(2, -20 * t + 10) * Math.sin((20 * t - 11.125) * ((2 * Math.PI) / 4.5))) / 2 + 1,
  easeInExpo: t => t === 0 ? 0 : Math.pow(2, 10 * t - 10),
  easeOutExpo: t => t === 1 ? 1 : 1 - Math.pow(2, -10 * t),
  easeInOutExpo: t => t === 0 ? 0 : t === 1 ? 1 : t < 0.5
    ? Math.pow(2, 20 * t - 10) / 2 : (2 - Math.pow(2, -20 * t + 10)) / 2,
  easeInCirc: t => 1 - Math.sqrt(1 - t * t),
  easeOutCirc: t => Math.sqrt(1 - Math.pow(t - 1, 2)),
  easeInOutCirc: t => t < 0.5
    ? (1 - Math.sqrt(1 - Math.pow(2 * t, 2))) / 2
    : (Math.sqrt(1 - Math.pow(-2 * t + 2, 2)) + 1) / 2,
  easeOutBounce,
  easeInBounce: t => 1 - easeOutBounce(1 - t),
  easeInOutBounce: t => t < 0.5
    ? (1 - easeOutBounce(1 - 2 * t)) / 2 : (1 + easeOutBounce(2 * t - 1)) / 2,
};

function easeOutBounce(t) {
  const n1 = 7.5625, d1 = 2.75;
  if (t < 1 / d1) return n1 * t * t;
  if (t < 2 / d1) return n1 * (t -= 1.5 / d1) * t + 0.75;
  if (t < 2.5 / d1) return n1 * (t -= 2.25 / d1) * t + 0.9375;
  return n1 * (t -= 2.625 / d1) * t + 0.984375;
}

// A parameterised easing written in the DSL — ease:in(1.05) — reaches us as the
// parser's funcCall node, not a string. The power family is anime's own
// (in(p) = t^p, out(p) = 1-(1-t)^p, inOut(p) halves each), and doing it here
// rather than through anime keeps it available with no bundle loaded — which is
// what makes it testable. Anything else is handed to anime's factory.
const POWER = {
  in:    p => t => Math.pow(t, p),
  out:   p => t => 1 - Math.pow(1 - t, p),
  inOut: p => t => t < 0.5
    ? Math.pow(2 * t, p) / 2
    : 1 - Math.pow(2 - 2 * t, p) / 2,
};

/** Is this the parser's funcCall node — ease:in(1.05), ease:outElastic(1, .5)? */
const isFuncCall = e => !!e && typeof e === 'object' && e.type === 'funcCall' && e.name;

/**
 * Look up an easing function by name, index, or pass through a function
 */
export function getEasing(ease) {
  if (typeof ease === 'function') return ease;
  if (typeof ease === 'number') {
    const names = Object.keys(easings);
    return easings[names[ease % names.length]] || easings.easeInOutSine;
  }
  if (isFuncCall(ease)) {
    const args = (ease.args || []).map(Number);
    const power = POWER[ease.name];
    if (power && Number.isFinite(args[0])) return power(args[0]);
    const factory = window.anime?.eases?.[ease.name];
    const f = typeof factory === 'function' ? factory(...args) : null;
    return typeof f === 'function' ? f : easings.easeInOutSine;
  }
  // "inSine" and "easeInSine" name the same curve — anime.js v4 dropped the prefix.
  const name = String(ease ?? '');
  if (!name) return easings.easeInOutSine;
  const fn = easings[name] || easings['ease' + name[0].toUpperCase() + name.slice(1)];
  if (fn) return fn;
  // Anything else is anime's: plain curves only (outInQuad, inQuint…) — the
  // parametric ones (outElastic, in, …) are factories with arity 2.
  const f = window.anime?.eases?.[animeEase(name)];
  return f?.length === 1 ? f : easings.easeInOutSine;
}

// easeInOutQuad → inOutQuad: anime v4 dropped the prefix; the rest
// (linear, outBack, outElastic(1, .5)) it parses itself — including the
// parameterised form, which is why a funcCall node is spelled back out rather
// than String()-ed into "[object Object]".
export const animeEase = e => isFuncCall(e)
  ? `${e.name}(${(e.args || []).join(',')})`
  : String(e).replace(/^ease([A-Z])/, (_, c) => c.toLowerCase());
